import Language               from '@/js/Shared/Language/Language';
import MessagesCollection     from '../Collections/MessagesCollection';
import MessageItem            from '../Entities/Message/MessageItem';
import MessageRecipientItem   from '../Entities/Message/MessageRecipientItem';
import MessageAttachmentsItem from '../Entities/Message/MessageAttachmentsItem';
import ViewportCollection     from '../Collections/ViewportCollection';
import MailboxesIds           from '../Helpers/MailboxesIds';

export {ViewportHTTPMapping as default};

/**
* Handles All Folder Messages Data
*
* @author      rsimoes <ruben-r-simoes@telecom.pt>
* @copyright   Copyright (C) Sapo. All rights reserved.
* @license     Private
* @since       7.0.0
*/
class ViewportHTTPMapping {
  /**
   * Maps Viewport server results
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ViewportCollection} viewport
   * @param {Object}             results server results
   *
   * @return {void}
   */
  mapViewport(viewport, results) {
    if (!results.view) {
      return;
    }

    let messages = viewport.get(results.view);

    if (results.data_reset === true) {
      messages.reset();
    }

    this.mapMessagesParams(messages, results);

    if (results.data) {
      this.mapMessagesData(messages, results.data);
    }

    if (results.disappear) {
      for (let id in results.disappear) {
        messages.delete(results.disappear[id]);
      }
    }

    if (results.rowlist) {
      messages.sortItems(results.rowlist);
    }

    viewport.add(messages);
  }

  /**
   * Updates Message Delivery notification
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ViewportCollection} viewport
   * @param {Object}             results server results
   *
   * @return {void}
   */
  mapMDN(viewport, results) {
    if (!results.puids || results.puids.length === 0) {
      return;
    }


    results.puids.forEach(item => {
      let message      = viewport.get(item.mbox).get(item.uid);
      message.mdn.sent = true;
    });
  }

  /**
   * Maps a message
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ViewportCollection} viewport
   * @param {Object}             results server results
   *
   * @return {void}
   */
  mapMessage(viewport, results) {
    results.forEach(mailbox => {
      if (!mailbox.mbox || !mailbox.buid) {
        return;
      }

      let message = viewport.get(mailbox.mbox).get(mailbox.buid);
      this.mapMessageBody(message, mailbox.data);
    });
  }

  /**
   * Maps server flags results
   *
   * @public
   *
   * @since 7.0.0
   *
   * @param {ViewportCollection} viewport
   * @param {Object}             results server results
   *
   * @return {void}
   */
  mapFlags(viewport, results) {
    if (!results.length) {
      return;
    }

    results.forEach(flags => {
      if (!flags.buids) {
        return;
      }

      for (let folder_id in flags.buids) {
        let ids      = MailboxesIds.getIdsAsArray(flags.buids[folder_id]);
        let messages = viewport.get(folder_id);

        ids.forEach((id) => {
          let message = messages.get(id);

          if (flags.remove) {
            this.mapMessageFlagsRemove(message, flags.remove);
          }

          if (flags.add) {
            this.mapMessageFlags(message, flags.add);
          }
        })
      }
    });
  }

  /**
   * Maps Messages Collection params from server results
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {MessagesCollection} messages messages collection
   * @param {Object}             results  server results
   *
   * @return {void}
   */
  mapMessagesParams(messages, results) {
    messages.folder_total = results.totalrows  || messages.folder_total;
    messages.cache_id     = results.cacheid    || messages.cacheid;
    messages.folder_id    = results.view       || messages.folder_id;
    messages.uidnext      = results.uidnext    || messages.uidnext;
    messages.error        = results.error      || false;
    messages.range_list   = results.rangelist  || [];

    if (results.metadata) {
      messages.sort_direction = results.metadata.sortdir;
      messages.sort_by        = results.metadata.sortby;
    }

    if (results.label) {
      let split             = results.label.split('.');
      messages.folder_label = split[split.length -1];
    }

    if (messages.folder_label.includes('impsearch')) {
      messages.folder_label = Language.say('messages.search.results_title');
    }
  }

  /**
   * Maps Messages data
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {MessagesCollection} messages messages collection
   * @param {Object}             data     server messages results
   *
   * @return {void}
   */
  mapMessagesData(messages, data) {
    for (let id in data) {
      let message = messages.get(id);
      this.mapMessageHeaders(message, id, data[id])
      this.mapMessageFlags(message, data[id].flag)
      messages.add(message);
    }
  }

  /**
   * Maps message headers server data into a message item object
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {MessageItem} message message item object
   * @param {Number}      id      message id
   * @param {Object}      data    server message data
   *
   * @return {void}
   */
  mapMessageHeaders(message, id, data) {
    message.id           = id;
    message.size         = data.size      || null;
    message.date.short   = data.date      || null;
    message.date.raw     = data.dateRaw   || null;
    message.subject      = data.subject   || null;
    message.sender_label = data.fromlabel || null;

    if (data.from && data.from.length > 0) {
      message.sender = this.getMessageRecipientItem(data.from[0]);
    }
  }

  /**
   * Maps message body data sent by the server into the message item object
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {MessageItem} message message item object
   * @param {Object}      data    server object data
   *
   * @return {void}
   */
  mapMessageBody(message, data) {
    if ((data.error || (!data.from && data.to && data.to.raw))) {
      message.invalid = true;
      return;
    }

    message.full_fetch    = true;
    message.body_parts    = data.msgtext   || data.body;
    message.date.local    = data.localdate || null;
    message.date.full     = data.fulldate  || null;
    message.download      = data.save_as   || null;
    message.print_id      = data.print_id  || null;
    message.mdn.requested = data.send_mdn  || false;

    // remove [ and ] from string 
    message.date.full = message.date.full.replace('[', '').replace(']', '');

    if (!message.id) {
      message.id = data.buid;
    }

    this.mapMessageRecipientsList(message, data);
    this.mapMessageAttachments(message, data);

    if (data.md) {
      data.md.forEach((item) => {
        if (!item[0] || item[0] !== 'html') {
          return;
        }

        message.body[item[1]] = {
          id       : item[1],
          contents : item[2],
        }
      })
    }
  }

  /**
   * Creates a new message attachaments list array with with data sent by the server
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {MessageItem} message message item object
   * @param {Object}      data    server data
   *
   * @return {void}
   */
  mapMessageAttachments(message, data) {
    if (!data.atc || !data.atc.list) {
      return;
    }

    message.attachments.download = data.atc.download;

    data.atc.list.forEach((atc) => {
      let entity           = new MessageAttachmentsItem();
      entity.id            = Number(atc.id);
      entity.muid          = atc.muid            || null;
      entity.download      = atc.download_url    || null;
      entity.preview_url   = atc.preview_url     || entity.download    || null;
      entity.thumbnail_url = atc.thumbnail_url   || entity.preview_url || null;
      entity.size          = atc.size            || null;
      entity.name          = atc.description_raw || null;
      entity.type          = atc.icon            || null;

      message.attachments.add(entity);
    })
  }


  /**
   * Creates a new message recipient entity with data sent by the server
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {MessageItem} message message item object
   * @param {Object}      data    server message data
   *
   * @return {void}
   */
  mapMessageRecipientsList(message, data) {
    if (data.from && data.from.addr.length > 0) {
      message.recipients.from = this.getMessageRecipientItem(data.from.addr[0]);
    }

    if (data.to.raw) {
      message.recipients.to.push(this.getMessageRecipientItem(data.to));
    }

    if (data.to && data.to.addr) {
      data.to.addr.forEach((to) => {
        message.recipients.to.push(this.getMessageRecipientItem(to));
      })
    } else {
      message.recipients.to.push(this.getMessageRecipientItem({
        v : Language.say('messages.sender_label.empty_from'),
      }));
    }

    if (data.cc && data.cc.addr) {
      data.cc.addr.forEach((cc) => {
        message.recipients.cc.push(this.getMessageRecipientItem(cc));
      })
    }

    if (data.bcc && data.bcc.addr) {
      data.bcc.addr.forEach((bcc) => {
        message.recipients.bcc.push(this.getMessageRecipientItem(bcc));
      })
    }

    message.recipients.count = message.recipients.to.length
                             + message.recipients.cc.length
                             + message.recipients.bcc.length;
  }

  /**
   * Creates and maps a recipient item object
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {Object} data recipient server data
   *
   * @return {void}
   */
  getMessageRecipientItem(data) {
    let recipient = new MessageRecipientItem();

    if (data === null) {
      return recipient;
    }

    recipient.raw   = data.v || data.raw || null;
    recipient.name  = data.p || data.raw || null;
    recipient.email = data.b || null;

    return recipient;
  }

  /**
   * Maps message entity flags with data sent by the server
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {MessageItem} message message item object
   * @param {Object}      data    server message data
   *
   * @return {void}
   */
  mapMessageFlags(message, data) {
    data.forEach(flag => {
      if (flag === '\\seen') {
        message.flags.read = true;
        return;
      }

      if (flag === 'unseen') {
        message.flags.read = false;
        return;
      }

      if (flag === 'attach') {
        message.flags.attachments = true;
        return;
      }

      if (flag === '\\flagged') {
        message.flags.flagged = true;
        return;
      }

      if (flag === '$forwarded') {
        message.flags.forwarded = true;
        return;
      }

      if (flag === '\\answered') {
        message.flags.answered = true;
        return;
      }

      if (flag === 'highp') {
        message.flags.priority_high = true;
        return;
      }

      if (flag === 'lowp') {
        message.flags.priority_low = true;
        return;
      }
    });
  }

  /**
   * Removes message flags data sent by the server into the current messages indexes
   *
   * @private
   *
   * @since 7.0.0
   *
   * @param {MessageItem} message message item object
   * @param {Object}      data    server message data
   *
   * @return {void}
   */
  mapMessageFlagsRemove(message, data) {
    data.forEach(flag => {
      if (flag === '\\seen') {
        message.flags.read = false;
      }

      if (flag === '\\flagged') {
        message.flags.flagged = false;
      }
    });
  }
}